import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import Link from 'next/link';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { Mixins } from 'planoplan-ui-kit/core/mixins';
import { useDebounce } from '@libs';
import { getHelpSearch } from '@modules/get-data';
import { useTranslations } from '@libs/translations';
import { Points, textSize } from '@styles';
import { HELP_CENTER } from '@constants';

const TIMEOUT_IN_MS = 300;
const MAX_QUERY_LENGTH = 140;

export const SearchField = ({ isSearchActive, searchActiveHandle, selectedSection }) => {
  const [query, setQuery] = useState('');
  const [isSearchFocus, setIsSearchFocus] = useState(false);
  const [isWidgetInitial, setIsWidgetInital] = useState(false);
  const debouncedQuery = useDebounce(query, TIMEOUT_IN_MS);
  const [results, setResults] = useState(null);
  const dispatch = useDispatch();
  const translations = useTranslations();

  useEffect(() => {
    if (!isWidgetInitial && window.usedeskMessenger && typeof window.usedeskMessenger.open === 'function') {
      setIsWidgetInital(true);
    }

    const search = async () => {
      const trimmed = debouncedQuery.trim();
      if (trimmed.length === 0) {
        setResults(null);
        return;
      }

      const data = selectedSection
        ? await dispatch(getHelpSearch({ query: encodeURIComponent(trimmed), selectedSection: selectedSection }))
        : await dispatch(getHelpSearch({ query: encodeURIComponent(trimmed) }));
      setResults(data);
    };

    search();
  }, [debouncedQuery, dispatch]);

  const cancelClickHandler = () => {
    setQuery('');
    searchActiveHandle(false);
  };

  return (
    <Wrap>
      <SearchRow isSearchActive={isSearchActive}>
        <SearchLabel isSearchActive={isSearchActive}>
          <SearchInput
            type="text"
            value={query}
            onFocus={() => {
              setIsSearchFocus(true);
              searchActiveHandle(true);
            }}
            onBlur={() => {
              setTimeout(() => setIsSearchFocus(false), 100);
            }}
            onChange={(e) => {
              e.target.value.length <= MAX_QUERY_LENGTH
                ? setQuery(e.target.value)
                : setQuery(e.target.value.slice(0, MAX_QUERY_LENGTH));
            }}
            placeholder={translations.t('site.help.searchPlaceholder')}
            isSearchActive={isSearchActive}
          />
          {query.length > 0 && <ClearQueryBtn onClick={() => setQuery('')} isSearchFocus={isSearchFocus} />}
        </SearchLabel>
        {isSearchActive && <ResetBtn onClick={cancelClickHandler}>{translations.t('site.help.resetButton')}</ResetBtn>}
        {results && isSearchActive && (
          <SearchResult>
            <SearchResultWrapper>
              {results.length > 0 ? (
                results.map((itemSection) => (
                  <SearchResultPart key={itemSection.section.title}>
                    {!selectedSection && <ResultTitle>{itemSection.section.title}</ResultTitle>}
                    <ResultList>
                      {itemSection.pages.map((item, idx) => (
                        <ResultListItem key={item.id || idx}>
                          <Link href={`/${HELP_CENTER.MAIN}/${item.slug}`} passHref>
                            <a aria-label={item.caption}>
                              <Caption>{item.caption}</Caption>
                            </a>
                          </Link>
                        </ResultListItem>
                      ))}
                    </ResultList>
                  </SearchResultPart>
                ))
              ) : (
                <SearchResultPart>
                  <ResultList>
                    <ResultListItem isHoverUnavailable={true}>
                      <Description>
                        {translations.t('site.help.nothingFoundSearch').replace('[search_query]', query)}
                      </Description>
                    </ResultListItem>
                  </ResultList>
                </SearchResultPart>
              )}
              {isWidgetInitial && (
                <SearchResultPart>
                  <SupportItem onClick={() => window.usedeskMessenger.open('chat')}>
                    <Caption>{translations.t('site.help.supportSearchTitle')}</Caption>
                    <Description>{translations.t('site.help.supportSearchDescription')}</Description>
                  </SupportItem>
                </SearchResultPart>
              )}
            </SearchResultWrapper>
          </SearchResult>
        )}
      </SearchRow>
    </Wrap>
  );
};

const Wrap = styled.div`
  margin: 30px auto 0;
  width: 100%;

  @media ${Points.tablet_landscape} {
    width: 600px;
    max-height: 600px;
  }

  @media ${Points.laptop} {
    width: 628px;
  }

  @media ${Points.fullhd} {
    width: 700px;
  }
`;

const SearchRow = styled.div`
  display: grid;
  grid-template-columns: auto min-content;
  gap: ${(p) => p.isSearchActive && '0 10px'};
  width: 100%;
`;

const SearchLabel = styled.label`
  position: relative;
  width: ${(p) => (p.isSearchActive ? '100%' : '250px')};
  margin: ${(p) => (p.isSearchActive ? '0' : '0 auto')};
`;

const SearchInput = styled.input`
  width: ${(p) => (p.isSearchActive ? '100%' : '250px')};
  padding: 5px 36px 5px 36px;
  border: 1px solid ${Colors.dark_200};
  border-radius: 8px;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'%3e%3cpath fill-rule='evenodd' clip-rule='evenodd' d='M16.6179 9.86069C16.6179 13.5926 13.5926 16.6179 9.86069 16.6179C6.1288 16.6179 3.10352 13.5926 3.10352 9.86069C3.10352 6.1288 6.1288 3.10352 9.86069 3.10352C13.5926 3.10352 16.6179 6.1288 16.6179 9.86069ZM14.9809 15.6881C13.6148 16.8893 11.8228 17.6179 9.86069 17.6179C5.57652 17.6179 2.10352 14.1449 2.10352 9.86069C2.10352 5.57652 5.57652 2.10352 9.86069 2.10352C14.1449 2.10352 17.6179 5.57652 17.6179 9.86069C17.6179 11.8228 16.8893 13.6148 15.6881 14.9809L20.7922 20.0851C20.9875 20.2804 20.9875 20.5969 20.7922 20.7922C20.5969 20.9875 20.2804 20.9875 20.0851 20.7922L14.9809 15.6881Z' fill='%23868686'/%3e%3c/svg%3e");
  background-position: 10px 50%;
  background-repeat: no-repeat;
  background-size: 24px 24px;
  ${textSize('body')};

  &::placeholder {
    color: ${Colors.dark_500};
  }

  &:hover {
    border-color: ${Colors.blue_500};
  }

  &:focus {
    border: none;
    border: 1px solid ${Colors.blue_500};
    width: 100%;
  }

  &:focus-visible {
    outline: none;
    border: 1px solid ${Colors.blue_500};
    width: 100%;
    color: ${Colors.main_dark};
  }

  @media ${Points.tablet_landscape} {
    padding: 10px 36px 10px 36px;
  }
`;

const SearchResult = styled.div`
  width: 100%;
  grid-column: span 2;

  @media ${Points.tablet_landscape} {
    position: relative;
    grid-column: 1;
    box-sizing: border-box;
    box-shadow: 0px 2px 4px 0px #00000014, 0px 0px 6px 0px #00000005;
    border-radius: 0 0 12px 12px;

    &::before {
      content: '';
      height: 20px;
      width: 100%;
      z-index: -1;
      position: absolute;
      top: -15px;
      box-sizing: border-box;
      box-shadow: -4px 0px 4px -4px rgba(0, 0, 0, 0.1), 4px 0px 4px -4px rgba(0, 0, 0, 0.1);
    }

    &::after {
      content: '';
      left: 6%;
      position: absolute;
      width: 88%;
    }
  }
`;
const SearchResultWrapper = styled.div`
  width: 100%;
  grid-column-start: span 2;
  padding: 20px 0;

  &::-webkit-scrollbar {
    scroll-margin-top: 10px;
    scroll-margin-bottom: 10px;
    padding-left: 10px;
    width: 4px;
    height: 90%;
    border-radius: 20px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${Colors.dark_200};
    border-radius: 18px;
    margin: 10px;
    cursor: pointer;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${Colors.dark_300};
  }

  &::-webkit-scrollbar-button {
    display: none;
  }

  @media ${Points.tablet_landscape} {
    overflow-y: auto;
    grid-column-start: 1;
    padding: 25px 20px 20px;
    max-height: calc(600px - 44px - 20px);
  }

  @media ${Points.laptop_wide} {
    max-height: calc(628px - 44px);
  }

  @media ${Points.fullhd} {
    max-height: calc(700px - 44px);
  }
`;

const SearchResultPart = styled.div`
  &:last-child ul {
    border-bottom: none;
    margin-bottom: 0;
  }
`;
const ResultTitle = styled.div`
  padding: 10px;
  color: ${Colors.dark_500};
  ${textSize('subtitle')};

  @media ${Points.tablet_landscape} {
    padding: 10px 15px;
  }
`;
const ResultList = styled.ul`
  ${Mixins.resetList()};
  border-bottom: 1px solid ${Colors.dark_200};
  margin-bottom: 20px;

  @media ${Points.tablet_landscape} {
    padding-bottom: 10px;
  }
`;
const ResultListItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: ${(p) => (p.isHoverUnavailable ? '0 10px 10px' : '10px')};
  border-radius: 10px;

  @media ${Points.tablet_landscape} {
    padding: ${(p) => (p.isHoverUnavailable ? '0 15px 15px' : '15px')};
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: ${(p) => (p.isHoverUnavailable ? 'none' : Colors.beige_100)};
    }
  }

  &:active {
    background-color: ${(p) => (p.isHoverUnavailable ? 'none' : Colors.main_beige)};
  }

  & a {
    ${Mixins.resetLink()};
  }
`;
const Caption = styled.h2`
  ${textSize('article')};
  font-weight: 300;
`;
const Description = styled.p`
  ${textSize('description')};
`;
const ClearQueryBtn = styled.button`
  ${Mixins.resetButton()};
  position: absolute;
  right: 10px;
  top: 4.2px;
  width: 24px;
  height: 24px;
  background-image: ${(p) =>
    p.isSearchFocus
      ? `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'%3e%3cpath d='M6.82353 17L17.1765 6M6.5 6.32353L17.5 16.6765' stroke='%23868686' stroke-linecap='round'/%3e%3c/svg%3e")`
      : 'none'};
  background-position: 50%;
  background-repeat: no-repeat;

  @media ${Points.tablet_landscape} {
    top: 9.2px;
  }

  @media ${Points.fullhd} {
    top: 9.2px;
  }
`;
const ResetBtn = styled.button`
  ${Mixins.resetButton()};
  color: ${Colors.main_dark};
`;

const SupportItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;

  @media ${Points.tablet_landscape} {
    padding: 15px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: ${Colors.beige_100};
    }
  }
`;
